import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalCita from "../components/ModalCita";
import FichaResumenPropiedad from "../components/propiedades/FichaResumenPropiedad";

const CitaModalOverlayContext = createContext(null);

export function useCitaModalOverlay() {
  const ctx = useContext(CitaModalOverlayContext);
  if (!ctx) {
    throw new Error("useCitaModalOverlay debe usarse dentro de CitaModalOverlayProvider");
  }
  return ctx;
}

/** Devuelve null si el provider no está montado (p. ej. tests). */
export function useCitaModalOverlayOptional() {
  return useContext(CitaModalOverlayContext);
}

/**
 * Traslada el ModalCita a una capa global, navega a la ficha y deja el modal encima
 * con overlay claro (fichaDetras).
 */
export function CitaModalOverlayProvider({ children }) {
  const navigate = useNavigate();
  const [overlay, setOverlay] = useState(null);
  const [vistaPreviaId, setVistaPreviaId] = useState(null);

  const clearOverlay = useCallback((options = {}) => {
    setOverlay((current) => {
      if (options.navigateReturn && current?.returnTo) {
        navigate(current.returnTo);
      }
      return null;
    });
    setVistaPreviaId(null);
  }, [navigate]);

  const transferModalToFicha = useCallback(({ path, returnTo, modalProps, onCloseLocal }) => {
    if (!path || !modalProps) return;
    onCloseLocal?.();
    setVistaPreviaId(null);
    setOverlay({
      returnTo: returnTo || "/agenda",
      modalProps,
    });
    navigate(path);
  }, [navigate]);

  const appendPropertyToCitaModal = useCallback((prop) => {
    if (!prop?.id) return false;
    let added = false;
    setOverlay((current) => {
      if (!current) return current;
      const list = current.modalProps?.properties || [];
      if (list.some((p) => String(p.id) === String(prop.id))) return current;
      added = true;
      return {
        ...current,
        modalProps: {
          ...current.modalProps,
          properties: [...list, prop],
        },
      };
    });
    return added;
  }, []);

  const isPropertyInCitaModal = useCallback((propId) => {
    if (!propId || !overlay?.modalProps?.properties) return false;
    return overlay.modalProps.properties.some((p) => String(p.id) === String(propId));
  }, [overlay?.modalProps?.properties]);

  const overlayModalProps = useMemo(() => {
    if (!overlay?.modalProps) return null;
    const mp = overlay.modalProps;

    const cerrarOverlay = (navigateReturn = false) => {
      mp.onClose?.();
      clearOverlay({ navigateReturn });
    };

    return {
      ...mp,
      isOpen: true,
      fichaDetras: true,
      zIndex: mp.zIndex ?? 1400,
      onClose: () => cerrarOverlay(true),
      onConfirm: async (...args) => {
        await mp.onConfirm?.(...args);
        clearOverlay();
      },
      onAbrirFichaCliente: (clienteId) => {
        if (clienteId) navigate(`/clientes/${clienteId}`);
      },
      onAbrirFichaContacto: (contactoId) => {
        if (contactoId) navigate(`/contactos/${contactoId}`);
      },
      onAbrirVistaPrevia: (propId) => {
        if (typeof mp.onAbrirVistaPrevia === "function") {
          mp.onAbrirVistaPrevia(propId);
        } else {
          setVistaPreviaId(propId);
        }
      },
    };
  }, [overlay, navigate, clearOverlay]);

  const value = useMemo(
    () => ({
      transferModalToFicha,
      clearOverlay,
      appendPropertyToCitaModal,
      isPropertyInCitaModal,
      isOverlayActive: Boolean(overlay),
    }),
    [transferModalToFicha, clearOverlay, appendPropertyToCitaModal, isPropertyInCitaModal, overlay],
  );

  return (
    <CitaModalOverlayContext.Provider value={value}>
      {children}
      {overlayModalProps ? <ModalCita {...overlayModalProps} /> : null}
      {vistaPreviaId ? (
        <FichaResumenPropiedad propiedadId={vistaPreviaId} onClose={() => setVistaPreviaId(null)} />
      ) : null}
    </CitaModalOverlayContext.Provider>
  );
}
